<template>
  <Page index class="index">
    <div class="categories">
      <nuxt-link
        v-for="category in categories"
        :key="category.id"
        :to="`/support/${category.slug}/`"
        class="category-card sm-5"
      >
        <div class="icon">
          <component :is="`Icon${category.icon}`" />
        </div>
        <h2 class="name">
          {{ category.name }}
        </h2>
      </nuxt-link>
    </div>
  </Page>
</template>

<script>
import jsonldDS from '@itbpbg/lms-components/src/utils/json-ld-ds';
import jsonldFA from '@itbpbg/lms-components/src/utils/json-ld-fa';
import global from '@itbpbg/lms-components/src/mixins/global';
import Page from '../../components/HelpCenter/Page';
import Iconaccount from '../../assets/svg/icon-support-account.svg?vue-component';
import Iconcertificate from '../../assets/svg/icon-support-certificate.svg?vue-component';
import Iconcreditcard from '../../assets/svg/icon-support-creditcard.svg?vue-component';
import Iconplay from '../../assets/svg/icon-support-play.svg?vue-component';
import Iconrocket from '../../assets/svg/icon-support-rocket.svg?vue-component';
import Iconclp from '../../assets/svg/icon-support-clp.svg?vue-component';

export default {
  components: {
    Page, Iconaccount, Iconcertificate, Iconcreditcard, Iconplay, Iconrocket, Iconclp,
  },
  mixins: [global],
  async asyncData({ $axios, route }) {
    const [categories, page] = await Promise.all([
      $axios.$get('/help-center/categories').catch(() => ([])),
      $axios.$get(`/pages/${route.path}`),
    ]);

    return { categories, page };
  },
  head() {
    const jsonld = this.$config.platform === 'ds' ? jsonldDS : jsonldFA;
    const siteFullUrl = `${this.$config.siteUrl}${this.$route.fullPath}`;

    const readAction = jsonld.PotentialActions.consume.read;
    readAction.target = [siteFullUrl];

    const searchAction = jsonld.PotentialActions.search;
    searchAction.target = [`${this.$config.siteUrl}/support/q={search_term_string}`];

    const breadcrumb = [{
      name: 'Help Center',
      slug: '/support',
    }];

    const seoWebPage = {
      ...jsonld.WebPage,
      '@id': siteFullUrl,
      url: siteFullUrl,
      name: this.page.metaTitle,
      description: this.page.metaDescription,
      datePublished: this.dateIsoString(this.page.created),
      dateModified: this.dateIsoString(this.page.updated),
      potentialAction: [searchAction, readAction],
    };

    const seoWebSite = { ...jsonld.WebSite };

    const seoImageObject = {
      ...jsonld.ImageObject,
      '@id': this.page.image,
      url: this.page.metaImage ? `${this.$config.imagesUrl}/meta/${this.page.metaImage}` : 'https://365datascience.com/resources/facebook_open_graph.png',
      width: this.page.metaImageWidth,
      height: this.page.metaImageHeight,
      caption: this.page.title,
    };

    jsonld.CollectionPage.mainEntity.itemListElement = [];
    if (this.categories) {
      this.categories.forEach((item) => {
        const post = {
          '@type': 'ItemPage',
          name: item.name,
        };
        jsonld.CollectionPage.mainEntity.itemListElement.push(post);
      });
    }

    const title = `${this.page.title ? this.page.title : 'Help Center'} | 365 Data Science`;

    const jsonData = [
      {
        '@context': 'https://schema.org',
        '@graph': [seoWebSite, seoImageObject, seoWebPage, jsonld.CollectionPage],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: jsonld.JSONBreadcrumb(breadcrumb, this.$config.siteUrl),
      },
    ];

    if (this.page.robots) {
      if (this.page.robots === 'noindex') {
        this.page.robots = 'noindex, follow';
      } else if (this.page.robots === 'nofollow') {
        this.page.robots = 'index, nofollow';
      }
    } else {
      this.page.robots = 'index, follow';
    }

    this.page.fullPath = siteFullUrl;

    const meta = [
      ...this.metaProps(
        {
          ...this.page,
          ogTitle: this.page.ogTitle || this.page.metaTitle || this.page.title,
          ogImage: this.page.ogImage || this.page.metaImage,
          ogDescription: this.page.ogDescription || this.page.metaDescription,
          twitterTitle: this.page.twitterTitle || this.page.metaTitle || title,
          twitterImage: this.page.twitterImage || this.page.metaImage,
          twitterDescription: this.page.twitterDescription || this.page.metaDescription,
          robots: `${this.page.robots}, max-snippet:-1, max-image-preview:large, max-video-preview:-1`,
        },
      ),
    ];
    return {
      titleTemplate: title,
      link: [
        { rel: 'canonical', href: this.page.canonicalURL || this.canonicalURL() },
      ],
      meta,
      script: jsonData ? [
        {
          vmid: 'jsonld',
          type: 'application/ld+json',
          json: jsonData,
        },
      ] : [],
    };
  },
};
</script>

<style lang="scss">
.support-page.index {
  background-color: var(--main-light-shade-1);
  font-family: "Mazzard", sans-serif;
}

.index {
  .categories {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-top: var(--spacing-80);

    .category-card {
      width: 31.62%;
      border: 1px solid var(--main-light-shade-3);
      border-radius: 10px;
      text-align: center;
      padding: var(--spacing-60) 0;
      background: var(--true-white);
      margin: 0 var(--spacing-14) var(--spacing-md) var(--spacing-14);

      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        margin-right: 0;
      }

      &:nth-child(3) {
        margin-right: 0;
      }

      &:nth-child(4) {
        margin-left: 0;
      }

      &:hover {
        border-color: var(--accent);
        cursor: pointer;
        text-decoration: none;
      }

      .name {
        font-size: 18px;
        color: var(--main-dark-shade-0);
        font-weight: 500;
        margin-bottom: 0;
      }
    }
  }

  @include bp($bp-laptop) {
    .categories {
      .category-card {
        width: 31.3%;
      }
    }
  }

  @include bp($bp-tablet) {
    .categories {
      .category-card {
        margin: 0 var(--spacing-14) var(--spacing-md) var(--spacing-14);

        &:first-child {
          margin-left: var(--spacing-14);
        }

        &:last-child {
          margin-right: var(--spacing-14);
        }

        &:nth-child(3) {
          margin-right: var(--spacing-14);
        }
      }
    }
  }

  @include bp($bp-phone) {
    .categories {
      .category-card {
        width: 100%;
        margin-left: 0;
        margin-right: 0;
        margin-bottom: var(--spacing-16);

        &:last-of-type {
          margin-bottom: 0;
        }
      }
    }
  }

  .fa {
    @import '../../assets/sass/fa/components/helpcenter.scss';
  }
}
</style>
